import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { ProtocolDetail } from '@/types';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';

export default function ProtocolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [protocol, setProtocol] = useState<ProtocolDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReextracting, setIsReextracting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReextractModal, setShowReextractModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    eligibility: true,
    contraindications: true,
    dosing: true,
    counseling: false,
    sideEffects: false,
  });

  useEffect(() => {
    if (id) fetchProtocol();
  }, [id]);

  const fetchProtocol = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await api.getProtocolDetail(id!);
      setProtocol(data.protocol);
    } catch (err: any) {
      setError(err.message || 'Failed to load protocol');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!protocol) return;
    setIsApproving(true);
    try {
      await api.approveProtocol(protocol.id, approveNotes);
      navigate('/protocols');
    } catch (err: any) {
      setError(err.message || 'Failed to approve protocol');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!protocol || !rejectReason.trim()) return;
    setIsRejecting(true);
    try {
      await api.rejectProtocol(protocol.id, rejectReason);
      setShowRejectModal(false);
      navigate('/protocols');
    } catch (err: any) {
      setError(err.message || 'Failed to reject protocol');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleReextract = async () => {
    if (!protocol) return;
    setIsReextracting(true);
    try {
      await api.reextractProtocol(protocol.id);
      setShowReextractModal(false);
      navigate('/protocols');
    } catch (err: any) {
      setError(err.message || 'Failed to trigger re-extraction');
    } finally {
      setIsReextracting(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !protocol) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="text-red-500" size={24} />
          <div>
            <h3 className="font-medium text-red-900">Error loading protocol</h3>
            <p className="text-red-700">{error || 'Protocol not found'}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/protocols')}
          className="mt-4 text-red-700 hover:text-red-800 font-medium"
        >
          &larr; Back to protocols
        </button>
      </div>
    );
  }

  const content = protocol.extracted_content;
  const isPending = protocol.extraction_status === 'pending_review';

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/protocols')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to protocols
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {protocol.display_name || protocol.protocol_id}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                protocol.protocol_type === 'vaccine'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-purple-100 text-purple-800'
              }`}>
                {protocol.protocol_type}
              </span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">{protocol.therapy_class}</span>
              <span className="text-gray-500">•</span>
              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                protocol.extraction_status === 'pending_review'
                  ? 'bg-yellow-100 text-yellow-800'
                  : protocol.extraction_status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {protocol.extraction_status === 'pending_review' && <Clock size={12} className="mr-1" />}
                {protocol.extraction_status === 'approved' && <CheckCircle size={12} className="mr-1" />}
                {protocol.extraction_status === 'rejected' && <XCircle size={12} className="mr-1" />}
                {protocol.extraction_status.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          {isPending && (
            <div className="flex gap-3 mt-4 lg:mt-0">
              <button
                onClick={() => setShowRejectModal(true)}
                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
              >
                <XCircle size={18} className="inline mr-2" />
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={isApproving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isApproving ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : (
                  <CheckCircle size={18} className="inline mr-2" />
                )}
                Approve
              </button>
            </div>
          )}
          {/* Re-extract button for rejected protocols */}
          {protocol.extraction_status === 'rejected' && (
            <div className="flex gap-3 mt-4 lg:mt-0">
              <button
                onClick={() => setShowReextractModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw size={18} className="inline mr-2" />
                Re-extract Protocol
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Validation warnings */}
      {content?._validation_warnings && content._validation_warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />
            <div>
              <h4 className="font-medium text-yellow-800">Validation Warnings</h4>
              <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                {content._validation_warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Protocol content */}
      {content ? (
        <div className="space-y-4">
          {/* Eligibility */}
          <CollapsibleSection
            title="Eligibility"
            isOpen={expandedSections.eligibility}
            onToggle={() => toggleSection('eligibility')}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.eligibility.min_age_years && (
                <InfoItem label="Minimum Age" value={`${content.eligibility.min_age_years} years`} />
              )}
              {content.eligibility.max_age_years && (
                <InfoItem label="Maximum Age" value={`${content.eligibility.max_age_years} years`} />
              )}
              <InfoItem
                label="Pregnancy"
                value={content.eligibility.pregnancy || 'Not specified'}
                subValue={content.eligibility.pregnancy_notes}
              />
              <InfoItem
                label="Breastfeeding"
                value={content.eligibility.breastfeeding || 'Not specified'}
                subValue={content.eligibility.breastfeeding_notes}
              />
            </div>
          </CollapsibleSection>

          {/* Contraindications */}
          <CollapsibleSection
            title="Contraindications"
            isOpen={expandedSections.contraindications}
            onToggle={() => toggleSection('contraindications')}
          >
            <div className="space-y-4">
              {content.contraindications.allergies?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Allergies</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {content.contraindications.allergies.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {content.contraindications.conditions?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Conditions</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {content.contraindications.conditions.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Dosing */}
          <CollapsibleSection
            title="Dosing"
            isOpen={expandedSections.dosing}
            onToggle={() => toggleSection('dosing')}
          >
            <div className="space-y-4">
              {content.dosing.adult && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Adult Dosing</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <InfoItem label="Dose" value={content.dosing.adult.dose} />
                    <InfoItem label="Route" value={content.dosing.adult.route} />
                    <InfoItem label="Frequency" value={content.dosing.adult.frequency} />
                    {content.dosing.adult.instructions && (
                      <InfoItem label="Instructions" value={content.dosing.adult.instructions} />
                    )}
                  </div>
                </div>
              )}
              {content.dosing.pediatric && content.dosing.pediatric.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Pediatric Dosing</h4>
                  {content.dosing.pediatric.map((ped, i) => (
                    <div key={i} className="mb-3 pb-3 border-b last:border-0 last:mb-0 last:pb-0">
                      {ped.age_range && <p className="text-sm text-gray-600"><strong>Age:</strong> {ped.age_range}</p>}
                      <p className="text-sm text-gray-600"><strong>Dose:</strong> {ped.dose}</p>
                      <p className="text-sm text-gray-600"><strong>Frequency:</strong> {ped.frequency}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Counseling Points */}
          <CollapsibleSection
            title="Counseling Points"
            isOpen={expandedSections.counseling}
            onToggle={() => toggleSection('counseling')}
          >
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              {content.counseling_points?.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </CollapsibleSection>

          {/* Side Effects */}
          {content.side_effects && (
            <CollapsibleSection
              title="Side Effects"
              isOpen={expandedSections.sideEffects}
              onToggle={() => toggleSection('sideEffects')}
            >
              <div className="space-y-4">
                {content.side_effects.common?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Common</h4>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      {content.side_effects.common.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {content.side_effects.serious?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-700 mb-2">Serious</h4>
                    <ul className="list-disc list-inside text-red-600 space-y-1">
                      {content.side_effects.serious.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {content.side_effects.when_to_seek_care && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-sm text-red-700">
                      <strong>When to seek care:</strong> {content.side_effects.when_to_seek_care}
                    </p>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Notes */}
          {content.notes && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Additional Notes</h3>
              <p className="text-gray-600">{content.notes}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-gray-500">No extracted content available</p>
        </div>
      )}

      {/* Metadata */}
      <div className="mt-8 bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-3">Extraction Metadata</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Model</span>
            <p className="font-medium text-gray-900">{protocol.llm_model_id || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-500">Input Tokens</span>
            <p className="font-medium text-gray-900">{protocol.llm_input_tokens || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-500">Output Tokens</span>
            <p className="font-medium text-gray-900">{protocol.llm_output_tokens || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-500">Latency</span>
            <p className="font-medium text-gray-900">
              {protocol.extraction_latency_ms ? `${(protocol.extraction_latency_ms / 1000).toFixed(1)}s` : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Protocol</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this protocol extraction.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={4}
              placeholder="Enter rejection reason..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || isRejecting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isRejecting ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : null}
                Reject Protocol
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-extract Modal */}
      {showReextractModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Re-extract Protocol</h3>
            <p className="text-gray-600 mb-4">
              This will trigger a new extraction from the original PDF using Claude AI.
              The rejected version will be superseded when the new extraction completes.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> The new extraction will appear in the pending review queue
                once processing is complete (usually within 30-60 seconds).
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowReextractModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReextract}
                disabled={isReextracting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isReextracting ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : (
                  <RefreshCw size={18} className="inline mr-2" />
                )}
                Re-extract
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function InfoItem({
  label,
  value,
  subValue,
}: {
  label: string;
  value: string;
  subValue?: string;
}) {
  return (
    <div>
      <span className="text-gray-500 text-sm">{label}</span>
      <p className="font-medium text-gray-900">{value}</p>
      {subValue && <p className="text-sm text-gray-600 mt-1">{subValue}</p>}
    </div>
  );
}
